/**
 * BotBrain: thinks every ~100 ms and emits the same Commands a player would.
 * Priority (PLAN §7): dodge > kick > defensive > healer triage > movement + rotation.
 * Reaction time is modelled per stimulus: a cast, telegraph or condition only counts
 * once it has been visible for `skill.reactionMs`.
 */
import type { AbilityId, BotBrain, BotView, ClassId, Command, Ms, UnitId } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import { castableWhileMoving, pickChoice, validChoices, type AplChoice, type AplEntry } from './apl';
import { planDefensive } from './awareness/defensive';
import { planDodge, respectedDangers } from './awareness/dodge';
import { planHeal } from './awareness/healer';
import { planKick, type KitLookup } from './awareness/kick';
import { mechanicTarget, planDuty } from './awareness/mechanics'; // +encounters
import { angleDiff, angleOf, dist, sub } from './geometry';
import { kitFor, type ClassKit } from './kits';
import { createMemory, endThink, type BotMemory } from './memory';
import { chooseTarget, looseMob, planPosition } from './movement';
import { abilityLocked, cooldownReady, inAbilityRange, inMelee, meleeRange, perceive, simWorld, type Perception, type RUnit } from './perception';
import { learnedSkill, resolveSkill, witsSkill, type BotPresetName, type SkillInput } from './skill';
import type { Persona, PersonaMode } from '@keystone/content';
import { personaPreset } from '@keystone/content';
import { createPersonaBot } from './persona/brain'; // +personas
import { createGrindMemory, grindTarget, grindWantsHeal, planGrindPosition, type GrindHome, type GrindMemory, type GrindStatus } from './grind'; // +field
import type { GatherMode } from './gather'; // +prof

export const THINK_MS: Ms = 100;

export interface BotOptions {
  unitId: UnitId;
  classId: ClassId;
  skill?: SkillInput;
  seed: number;
  /** +personas: the simulated player behind this bot (chat, AFK, wandering, tilt; persona/brain.ts). */
  persona?: Persona;
  /** +personas: how far the persona may go (default 'companion': never quits or throws). */
  mode?: PersonaMode;
  /**
   * +personas: play at the persona's own skill (personaSkill) instead of `skill`. Default: true in
   * 'unhinged' mode, false in 'companion' mode (the player's chosen bot preset decides).
   */
  personaSkill?: boolean;
  /** +grid: Auto's wits from the Waystone Grid (skill.ts witsSkill; 0 / absent = unchanged). */
  wits?: number;
  /**
   * +field: solo grind mode (grind.ts). Default: on whenever the bot's map is an open field
   * (`DungeonDef.field.kind === 'field'`); true forces it on any map. Towns: the bot does nothing.
   */
  grind?: boolean;
}

/** +field: a bot that can report what its grind mode is doing (web Auto label). */
export interface GrindBrain extends BotBrain {
  grindStatus(): GrindStatus;
  /** +zone: where Auto hunts on a big open map (null = the whole map). */
  setGrindHome(home: GrindHome | null): void;
  /** +prof: the Auto setting: hunt (default), gather or both. */
  setGatherMode(mode: GatherMode): void;
}

/** +field: the grind status of a bot made by createBot (null for a persona-wrapped brain). */
export function grindStatusOf(brain: BotBrain): GrindStatus | null {
  return 'grindStatus' in brain && typeof (brain as GrindBrain).grindStatus === 'function' ? (brain as GrindBrain).grindStatus() : null;
}

/** +personas: a persona's bot skill (content personaPreset: skill trait → preset). */
export function personaSkill(persona: Pick<Persona, 'traits'>): BotPresetName {
  return personaPreset(persona);
}

/** A bot brain; with `persona` it is wrapped in the persona layer (a PersonaBrain). */
export function createBot(opts: BotOptions): BotBrain {
  if (!opts.persona) return createPlainBot(opts);
  const mode = opts.mode ?? 'companion';
  const own = opts.personaSkill ?? mode === 'unhinged';
  const inner = createPlainBot({ ...opts, ...(own ? { skill: personaSkill(opts.persona) } : {}) });
  return createPersonaBot({ inner, persona: opts.persona, mode, seed: opts.seed });
}

function createPlainBot(opts: BotOptions): GrindBrain {
  const skill = witsSkill(resolveSkill(opts.skill), opts.wits ?? 0); // +grid
  const rng = createSeededRng(opts.seed);
  const mem = createMemory(rng, skill);
  const kits = new Map<string, ClassKit | undefined>();
  // Small per-bot think offset so four bots don't all think on the same tick.
  let nextThinkAt: Ms = rng.int(0, 1) * 50;
  const grind = createGrindMemory(); // +field
  grind.restBias = skill.hpBias ?? 0; // +grid: Auto with wits rests a little earlier

  return {
    unitId: opts.unitId,
    skill,
    grindStatus: () => ({ ...grind.status }),
    setGrindHome: (home) => {
      grind.home = home;
      grind.roamTo = null;
      grind.huntId = null;
    },
    setGatherMode: (mode) => {
      grind.gather.mode = mode; // +prof
      grind.gather.target = null;
    },
    decide(view: BotView, now: Ms): Command[] {
      if (now < nextThinkAt) return [];
      nextThinkAt = now + THINK_MS;
      trackWipes(view, mem);
      // +field: towns are safe (nothing to do); open fields (or opts.grind) play the solo grind.
      const field = view.content.dungeon(view.world.dungeonId)?.field;
      if (field?.kind === 'town') {
        grind.status = { state: 'town', targetId: null };
        return [];
      }
      const grinding = opts.grind ?? field?.kind === 'field';
      const self = view.world.units[opts.unitId];
      if (!self?.alive) {
        if (grinding) grind.status = { state: 'dead', targetId: null };
        return [];
      }
      const kitOf: KitLookup = (u: RUnit) => {
        const id = u.classId ?? '';
        if (!kits.has(id)) kits.set(id, kitFor(id, view.content));
        return kits.get(id);
      };
      const kit = kitOf(self);
      if (!kit) return [];
      mem.now = now;
      // GCD slack (skill dial): drawn once per GCD/cast end.
      const freeAt = Math.max(self.gcdUntil, self.cast?.endsAt ?? 0);
      if (freeAt !== mem.slackFor) {
        mem.slackFor = freeAt;
        const [lo, hi] = mem.skill.gcdSlackMs;
        mem.nextPressAt = freeAt + (hi > lo ? mem.rng.range(lo, hi) : lo);
      }
      const p = perceive({ ...view, selfId: opts.unitId });
      // Wedged against a wall corner (the path ignores the unit's radius; the sim slides in place):
      // step sideways for a moment, then resume. Deterministic: alternates sides per attempt.
      const unstick = planUnstick(p, mem);
      if (unstick) return unstick;
      const cmds = think(p, kit, kitOf, mem, grinding ? grind : null);
      // Re-think exactly when the GCD or cast frees up (plus slack), if that's sooner.
      const pressAt = Math.max(freeAt, mem.nextPressAt);
      if (pressAt > now && pressAt < nextThinkAt) nextThinkAt = pressAt;
      else if (freeAt > now && freeAt < nextThinkAt) nextThinkAt = freeAt;
      const live = new Set<string>();
      for (const e of p.engaged) if (e.cast) for (const r of ['primary', 'backup']) live.add(`kick:${r}:${e.id}:${e.cast.startedAt}`);
      for (const d of p.dangers) live.add(`dodge:${d.key}`);
      endThink(mem, live, now);
      return cmds;
    },
  };
}

/**
 * Wipe-loop safety: count wipes per boss (a wipe while a boss encounter was the last one engaged
 * and not yet killed) and play that boss with the learned skill (skill.ts learnedSkill).
 */
function trackWipes(view: BotView, mem: BotMemory): void {
  const w = view.world;
  const L = mem.learn;
  const wipes = w.mplus?.wipes ?? 0;
  if (wipes > L.wipesSeen) {
    if (L.encounterId && !w.encountersDone.includes(L.encounterId)) L.wipes.set(L.encounterId, (L.wipes.get(L.encounterId) ?? 0) + (wipes - L.wipesSeen));
    L.wipesSeen = wipes;
  }
  const enc = w.encounter && w.encounter.result === null ? w.encounter.encounterId : null;
  if (enc) L.encounterId = enc;
  else if (L.encounterId && w.encountersDone.includes(L.encounterId)) L.encounterId = null;
  const n = enc ? (L.wipes.get(enc) ?? 0) : 0;
  if (n === L.applied) return;
  L.applied = n;
  mem.skill = learnedSkill(mem.baseSkill, n);
}

function think(p: Perception, kit: ClassKit, kitOf: KitLookup, mem: BotMemory, grind: GrindMemory | null = null): Command[] {
  const self = p.self;
  const control = p.view.queries.controlState(self.id);
  if (!control.canAct) return [];
  const cmds: Command[] = [];

  // 1. Dodge.
  // +field: the solo grind picks its own targets (grind.ts); dungeons use the group logic.
  const base = grind ? grindTarget(p, grind) : chooseTarget(p, kit, mem);
  const target = grind ? base : (mechanicTarget(p, kit, base) ?? base); // +encounters: kill-priority adds, never an immune boss
  // Dodging or an urgent duty: keep moving, but instants (and kicks) still go out on the run.
  const dodge = control.canMove ? planDodge(p, mem, p.tank?.pos) : null;
  let urgentMove = false;
  let duty: ReturnType<typeof planDuty> = null;
  if (dodge) {
    cmds.push(...dodge);
    urgentMove = true;
    const kick = planKick(p, kit, kitOf, mem);
    if (kick) {
      cmds.push(...kick.filter((c) => c.type !== 'cancelCast' && c.type !== 'move'));
      if (kick.some((c) => c.type === 'cast')) return cmds;
    }
  } else {
    // 1b. +encounters: urgent mechanic duties (tether, soak, behind, kite, look away, explode) beat kicks.
    duty = control.canMove ? planDuty(p, kit, mem, respectedDangers(p, mem)) : null;
    if (duty?.urgent) {
      cmds.push(...duty.cmds);
      urgentMove = true;
      const kick = planKick(p, kit, kitOf, mem);
      if (kick) {
        cmds.push(...kick.filter((c) => c.type !== 'cancelCast' && c.type !== 'move' && c.type !== 'face'));
        if (kick.some((c) => c.type === 'cast')) return cmds;
      }
      if (duty.kind === 'lookAway') return cmds;
    }

    // 2. Kick (an urgent duty already folded its kick in above).
    if (!duty?.urgent) {
      const kick = planKick(p, kit, kitOf, mem);
      if (kick) return kick;
    }
  }

  // 3. Defensives.
  const def = planDefensive(p, kit, mem);
  if (def) {
    cmds.push(...def.cmds);
    if (!def.offGcd) return cmds;
  }

  // Target + facing.
  if (target && self.targetId !== target.id) cmds.push({ type: 'target', actorId: self.id, targetId: target.id });
  // +autoattack: weapon classes start auto-attack on an engaged target, like a player would
  // (weapon abilities start it in the sim too; casters' staves only when idle in melee, below).
  if (target && self.swing && self.swing.kind !== 'staff' && !self.autoAttack && p.engaged.includes(target)) cmds.push({ type: 'autoAttack', actorId: self.id, on: true });

  // 4. Positioning (never walks into a respected danger).
  if (!urgentMove) {
    const dangers = respectedDangers(p, mem);
    if (duty) cmds.push(...duty.cmds); // +encounters: duties replace normal positioning
    else if (control.canMove) cmds.push(...(grind ? planGrindPosition(p, kit, mem, grind, target, dangers) : planPosition(p, kit, mem, target, dangers)));
  }
  // +prof: walking to / channelling a node: no buffs or casts (a cast would break the channel).
  if (grind?.status.state === 'gathering' && !urgentMove) return cmds;
  const moving = cmds.some((c) => c.type === 'move') || (!!self.move && urgentMove);

  // +ranger-specs: off-GCD reflexes (the Deadeye's Snap Shut inside the reload window) go out even while the GCD runs.
  if (kit.reflex && control.canCast) {
    const r = kit.reflex(p, mem);
    if (r) return [...cmds, ...r];
  }

  // +specs: an off-GCD stealth / buff before the next pull goes out even while the last fight's GCD
  // runs (the Venomwright's Creeping Fume, the Shadowdancer's Duskwalk: the kill's press would
  // otherwise hold it until the next mob had already noticed you).
  if (p.engaged.length === 0 && !self.cast && control.canCast && kit.oocBuffs?.length && !grind?.resting && p.view.queries.gcdRemaining(self.id) > 0) {
    const ctx = { p, target, moving, noFinesse: !mem.skill.aplFinesse, kickDiscipline: mem.skill.kickDiscipline, hpBias: mem.skill.hpBias ?? 0 };
    const buff = validChoices(kit.oocBuffs, ctx, 4).find((c) => c.ability.gcdMs === 0);
    if (buff) return withCast(p, cmds, buff, moving);
  }

  // Nothing else while the GCD or a cast is running (or during this bot's GCD slack).
  const busy = !!self.cast || p.view.queries.gcdRemaining(self.id) > 0 || p.now < mem.nextPressAt;
  if (busy || !control.canCast) return cmds;
  // Sloppy players often stop pressing buttons while they run.
  if (moving && mem.skill.moveIdleChance > 0 && mem.rng.chance(mem.skill.moveIdleChance)) return cmds;

  // 5. Healer triage.
  if (kit.role === 'healer' && (!grind || grindWantsHeal(p, grind))) {
    const heal = planHeal(p, kit, mem);
    // A cast-time heal stops the bot: never while dodging or on an urgent duty.
    if (heal && !(urgentMove && heal.some((c) => c.type === 'stop'))) return withoutMoveIfStopping(cmds, heal);
  }

  // Tank: taunt anything loose.
  if (kit.role === 'tank' && self.controller === 'bot' && !grind && kit.taunt) {
    const loose = looseMob(p);
    const tauntDef = p.view.content.ability(kit.taunt);
    if (loose && tauntDef && !abilityLocked(p.view, self, kit.taunt) && cooldownReady(self, tauntDef) && inAbilityRange(self, loose, tauntDef) && p.view.queries.canCast(self.id, kit.taunt, loose.id).ok) {
      cmds.push({ type: 'cast', actorId: self.id, abilityId: kit.taunt, targetId: loose.id });
      return cmds;
    }
  }

  // Tank: grip ranged casters into the pile.
  if (kit.role === 'tank' && (self.controller === 'bot' || !!grind) && kit.grip) {
    const caster = rangedCaster(p);
    if (caster && !abilityLocked(p.view, self, kit.grip) && p.view.queries.canCast(self.id, kit.grip, caster.id).ok) {
      const gripDef = p.view.content.ability(kit.grip);
      if (gripDef && inAbilityRange(self, caster, gripDef)) {
        cmds.push({ type: 'cast', actorId: self.id, abilityId: kit.grip, targetId: caster.id });
        return cmds;
      }
    }
  }

  // 5b. +basekits: out of combat: party buffs, stealth before a pull, run speed.
  if (p.engaged.length === 0) {
    if (kit.oocBuffs?.length && !grind?.resting) {
      const ctx = { p, target, moving, noFinesse: !mem.skill.aplFinesse, kickDiscipline: mem.skill.kickDiscipline, hpBias: mem.skill.hpBias ?? 0 };
      const buff = validChoices(kit.oocBuffs, ctx, 1)[0];
      if (buff) return withCast(p, cmds, buff, moving);
    }
  }

  // 6. Rotation.
  if (!target && kit.role !== 'healer') return cmds;
  if (grind) {
    if (!target) return cmds; // +field: no self-buff spam while resting or roaming
    // +field: before the pull, only a press that hits the target counts (no cooldowns into air).
    if (!p.engaged.includes(target)) {
      const ctx = { p, target, moving, noFinesse: !mem.skill.aplFinesse, kickDiscipline: mem.skill.kickDiscipline, hpBias: mem.skill.hpBias ?? 0 };
      const pull = validChoices(pullApl(kit), ctx, 12).find((c) => c.targetId === target.id && !(c.ability.cooldown && c.ability.cooldown.durationMs >= 30_000));
      return pull ? withCast(p, cmds, pull, moving) : cmds;
    }
  }
  const choice = pickChoice(kit.apl, { p, target, moving, noFinesse: !mem.skill.aplFinesse, kickDiscipline: mem.skill.kickDiscipline, hpBias: mem.skill.hpBias ?? 0 }, mem.skill.rotationAccuracy, mem.rng);
  if (!choice) {
    // +autoattack: a caster with nothing to cast and an engaged enemy in melee bonks it with the staff.
    if (target && self.swing?.kind === 'staff' && !self.autoAttack && p.engaged.includes(target) && inMelee(self, target)) cmds.push({ type: 'autoAttack', actorId: self.id, on: true });
    return cmds;
  }
  return withCast(p, cmds, choice, moving);
}

const PULL_APLS = new WeakMap<ClassKit, AplEntry[]>();

/** +field: the APL with its 'inCombat' gates dropped: the press that opens on an idle mob (healers' damage is all gated). */
function pullApl(kit: ClassKit): AplEntry[] {
  let apl = PULL_APLS.get(kit);
  if (!apl) {
    apl = kit.apl.map((e) => (e.when ? { ...e, when: e.when.filter((c) => c.type !== 'inCombat') } : e));
    PULL_APLS.set(kit, apl);
  }
  return apl;
}

/** No progress for this long while walking to a destination = stuck. */
const STUCK_MS: Ms = 1200;
const STUCK_EPS = 0.4;
const DETOUR_YD = 4;
const DETOUR_MS: Ms = 700;

function planUnstick(p: Perception, mem: BotMemory): Command[] | null {
  const self = p.self;
  const st = mem.stuck;
  if (p.now < st.detourUntil) return [];
  if (self.move?.mode !== 'dest' || !p.view.queries.controlState(self.id).canMove) {
    st.since = p.now;
    st.at = { ...self.pos };
    return null;
  }
  // Progress = getting closer to the destination (sliding back and forth along a wall is not).
  const goalD = dist(self.pos, self.move.dest);
  const refD = dist(st.at, self.move.dest);
  if (refD - goalD > STUCK_EPS || refD === 0) {
    st.since = p.now;
    st.at = { ...self.pos };
    return null;
  }
  if (p.now - st.since < STUCK_MS) return null;
  // Sideways (alternating) relative to where we were trying to go, then back off a little.
  const goal = self.move.dest;
  const base = angleOf(sub(goal, self.pos));
  st.attempt++;
  for (const turn of st.attempt % 2 ? [Math.PI / 2, -Math.PI / 2, Math.PI] : [-Math.PI / 2, Math.PI / 2, Math.PI]) {
    const a = base + turn;
    const q = { x: self.pos.x + Math.cos(a) * DETOUR_YD, y: self.pos.y + Math.sin(a) * DETOUR_YD };
    if (!p.view.queries.spatial.isWalkable(simWorld(p), q)) continue;
    st.since = p.now;
    st.detourUntil = p.now + DETOUR_MS;
    mem.lastMoveDest = null;
    return [{ type: 'move', actorId: self.id, dest: q }];
  }
  st.since = p.now;
  return null;
}

/** An engaged caster hanging back at range (prefers one mid-cast). */
function rangedCaster(p: Perception): RUnit | undefined {
  const far = p.engaged.filter((e) => {
    const def = e.npcId ? p.view.content.npc(e.npcId) : undefined;
    return !!def?.preferredRange && dist(e.pos, p.self.pos) > meleeRange(p.self, e) + 5;
  });
  return far.find((e) => e.cast?.interruptible) ?? far[0];
}

function withCast(p: Perception, cmds: Command[], choice: AplChoice, moving: boolean): Command[] {
  const self = p.self;
  const ab = choice.ability;
  if (!castableWhileMoving(p, ab)) {
    // Casting while repositioning: skip the cast if we must move, else make sure we stand still.
    if (moving) return cmds;
    if (self.move) cmds.push({ type: 'stop', actorId: self.id });
  }
  if (choice.targetId && choice.targetId !== self.id) faceIfNeeded(p, cmds, choice.targetId);
  cmds.push({ type: 'cast', actorId: self.id, abilityId: ab.id, ...(choice.targetId ? { targetId: choice.targetId } : {}), ...(choice.point ? { point: choice.point } : {}) });
  return cmds;
}

function faceIfNeeded(p: Perception, cmds: Command[], targetId: UnitId): void {
  const t = p.world.units[targetId];
  if (!t) return;
  const want = angleOf(sub(t.pos, p.self.pos));
  if (Math.abs(angleDiff(want, p.self.facing)) > 0.3) cmds.push({ type: 'face', actorId: p.self.id, targetId });
}

/** A cast-time heal wins over repositioning: drop pending moves when the heal stops us. */
function withoutMoveIfStopping(cmds: Command[], heal: Command[]): Command[] {
  const stops = heal.some((c) => c.type === 'stop');
  return [...(stops ? cmds.filter((c) => c.type !== 'move') : cmds), ...heal];
}

/** Ids of the abilities a bot would press for this class, for HUD/debug tooling. */
export function rotationAbilities(classId: string, view: Pick<BotView, 'content'>): AbilityId[] {
  return kitFor(classId, view.content)?.apl.map((e) => e.ability) ?? [];
}
